import { Component, input, output, forwardRef, signal, computed, effect, viewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonToggleGroup, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';

export type StatusType = 'Unresolved' | 'InProgress' | 'NotImportant' | 'Resolved';

export interface StatusOption {
  value: StatusType;
  icon: string;
  tooltip: string;
  color: string;
}

export const STATUS_OPTIONS: StatusOption[] = [
  {
      value: 'Unresolved',
      icon: 'bi bi-exclamation-circle-fill',
      tooltip: 'Unresolved',
      color: '',
    },
    {
      value: 'InProgress',
      icon: 'bi bi-cone-striped',
      tooltip: 'InProgress',
      color: '',
    },
    {
      value: 'NotImportant',
      icon: 'bi bi-info-circle-fill',
      tooltip: 'NotImportant',
      color: '',
    },
    {
      value: 'Resolved',
      icon: 'bi bi-check-circle',
      tooltip: 'Resolved',
      color: '',
    },
];

@Component({
  selector: 'app-lt-current-status-toggle',
  imports: [ 
    CommonModule, 
    FormsModule,
    MatButtonToggleModule,
    MatIconModule,
    MatTooltipModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LTCurrentStatusToggleComponent),
      multi: true,
    },
  ],
  templateUrl: './lt-current-status-toggle.html',
  styleUrl: './lt-current-status-toggle.scss',
  standalone: true,
})
export class LTCurrentStatusToggleComponent implements ControlValueAccessor {
  readonly value = input<StatusType | null | undefined>(undefined);
  readonly disabled = input<boolean>(false);
  readonly customOptions = input<StatusOption[]>([]);

  readonly statusChange = output<StatusType>();

  private readonly toggleGroup = viewChild(MatButtonToggleGroup);

  private internalValue = signal<StatusType | null>(null);

  currentValue = computed<StatusType | null>(() => {
    const bound = this.value();
    return bound !== undefined ? bound : this.internalValue();
  });

  statusOptions = computed(() => {
    const custom = this.customOptions();
    return custom.length > 0 ? custom : STATUS_OPTIONS;
  });

  selectedValues = computed(() => {
    const val = this.currentValue();
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  });

  isSelected = computed(() => {
    const selected = this.selectedValues();
    return (status: StatusType) => selected.includes(status);
  });

  // ControlValueAccessor
  onChange: any = () => {};
  onTouch: any = () => {};

  constructor() {
    effect(() => {
      this.syncToggleGroup(this.currentValue());
    });
  }

  writeValue(value: StatusType): void {
    this.internalValue.set(value);
    this.syncToggleGroup(this.currentValue());
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    //this.disabled = isDisabled;
  }

  onStatusChange(value: StatusType): void {
    this.internalValue.set(value);
    this.onChange(value);
    this.statusChange.emit(value);

    this.syncToggleGroup(this.currentValue());
  }

  private syncToggleGroup(value: StatusType | null): void {
    const group = this.toggleGroup();
    if (group && group.value !== value) {
      group.value = value;
    }
  }
}
